import { AlertCircle, AtSign, LoaderCircle, MailWarning, Plus, User, X } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { type Miembro, useAgregarAviso, useAvisos, useQuitarAviso } from "@/lib/obras";

// EMAIL_RE es una validación de forma razonable para el correo externo (algo@algo.tld).
// El backend valida de nuevo con net/mail; esto solo evita mandar disparates.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * AvisosPanel gestiona los destinatarios de los avisos LOTO de una obra (#199): una
 * lista de chips con quitar individual, más dos formas de añadir — (a) un miembro de
 * la obra que tenga email en su ficha y (b) un correo externo validado. Sin texto
 * libre a caraperro: cada destinatario es un chip. El backend es el guardián real
 * (RequireObraAdmin, unicidad case-insensitive por obra).
 */
export function AvisosPanel({ obraId, miembros }: { obraId: number; miembros: Miembro[] }) {
  const avisos = useAvisos(obraId);
  const agregar = useAgregarAviso(obraId);
  const quitar = useQuitarAviso(obraId);

  const [miembroSel, setMiembroSel] = useState("");
  const [correo, setCorreo] = useState("");
  const [errorMiembro, setErrorMiembro] = useState<string | null>(null);
  const [errorCorreo, setErrorCorreo] = useState<string | null>(null);

  // Correos ya en la lista (normalizados) para no ofrecer duplicados en el selector.
  const yaEnLista = useMemo(
    () => new Set((avisos.data?.destinatarios ?? []).map((d) => d.email.trim().toLowerCase())),
    [avisos.data],
  );

  // Estado del motor SMTP (#205): sin motor operativo los avisos no saldrán, pase lo que
  // pase con la lista; se avisa aquí, donde se gestiona. Solo cuando la carga trajo dato.
  const motorApagado = avisos.data != null && !avisos.data.motor_configurado;

  // Miembros con email en la ficha que aún no están en la lista de avisos.
  const miembrosDisponibles = useMemo(
    () =>
      miembros.filter(
        (m) =>
          m.email != null && m.email.trim() !== "" && !yaEnLista.has(m.email.trim().toLowerCase()),
      ),
    [miembros, yaEnLista],
  );

  async function onAgregarMiembro(e: FormEvent) {
    e.preventDefault();
    setErrorMiembro(null);
    const idUsuario = Number(miembroSel);
    const miembro = miembros.find((m) => m.id_usuario === idUsuario);
    if (!miembro || miembro.email == null || miembro.email.trim() === "") {
      setErrorMiembro("Elige un miembro con correo.");
      return;
    }
    try {
      await agregar.mutateAsync({ email: miembro.email.trim(), id_usuario: miembro.id_usuario });
      setMiembroSel("");
    } catch (err) {
      setErrorMiembro(mensajeError(err, "No se pudo añadir el miembro."));
    }
  }

  async function onAgregarCorreo(e: FormEvent) {
    e.preventDefault();
    setErrorCorreo(null);
    const email = correo.trim();
    if (!EMAIL_RE.test(email)) {
      setErrorCorreo("Escribe un correo válido.");
      return;
    }
    try {
      await agregar.mutateAsync({ email });
      setCorreo("");
    } catch (err) {
      setErrorCorreo(mensajeError(err, "No se pudo añadir el correo."));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-base text-slate-500">
        Quién recibe los avisos de eventos LOTO de esta obra (apertura, colocación, retirada y
        cierre). Cada obra tiene su propia lista.
      </p>

      {/* --- Estado del motor SMTP (#205): si está apagado, ningún aviso saldrá --- */}
      {motorApagado ? (
        <Alert variant="info">
          <MailWarning className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            El motor de correo del servidor no está configurado, así que los avisos de esta obra no
            se enviarán aunque añadas destinatarios. Se activará cuando el SMTP esté definido en el
            despliegue.
          </span>
        </Alert>
      ) : null}

      {/* --- Chips de destinatarios --- */}
      {avisos.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>No se pudieron cargar los destinatarios. Inténtalo de nuevo.</span>
        </Alert>
      ) : null}

      {avisos.isPending ? (
        <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando destinatarios…
        </div>
      ) : null}

      {avisos.data && avisos.data.destinatarios.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-10 text-center text-slate-500">
          Aún no hay destinatarios. Esta obra no enviará avisos hasta que añadas al menos uno.
        </div>
      ) : null}

      {avisos.data && avisos.data.destinatarios.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Destinatarios de avisos">
          {avisos.data.destinatarios.map((d) => {
            const deMiembro = d.id_usuario != null;
            const Icono = deMiembro ? User : AtSign;
            return (
              <li key={d.id_destinatario}>
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1 pr-1 pl-3 text-slate-700 text-sm">
                  <Icono className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                  <span>{d.email}</span>
                  <button
                    type="button"
                    aria-label={`Quitar ${d.email}`}
                    onClick={() => void quitar.mutateAsync(d.id_destinatario)}
                    disabled={quitar.isPending}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 disabled:opacity-50"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {quitar.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>No se pudo quitar el destinatario. Inténtalo de nuevo.</span>
        </Alert>
      ) : null}

      {/* --- Añadir --- */}
      <div className="grid gap-6 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2">
        {/* (a) Desde un miembro de la obra con email */}
        <form
          className="flex flex-col gap-1.5"
          onSubmit={(e) => void onAgregarMiembro(e)}
          noValidate
        >
          <Label htmlFor="aviso-miembro">Añadir un miembro</Label>
          <div className="flex gap-2">
            <Select
              id="aviso-miembro"
              value={miembroSel}
              aria-invalid={errorMiembro ? true : undefined}
              onChange={(e) => {
                setMiembroSel(e.target.value);
                setErrorMiembro(null);
              }}
            >
              <option value="">Elige un miembro…</option>
              {miembrosDisponibles.map((m) => (
                <option key={m.id_usuario} value={m.id_usuario}>
                  {`${m.nombre} ${m.apellidos}`.trim()} · {m.email}
                </option>
              ))}
            </Select>
            <Button type="submit" disabled={agregar.isPending || miembroSel === ""}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Añadir
            </Button>
          </div>
          {miembrosDisponibles.length === 0 ? (
            <p className="text-slate-500 text-sm">
              No hay miembros con correo disponibles. Añade el correo en la ficha del usuario o usa
              un correo externo.
            </p>
          ) : null}
          <FieldError>{errorMiembro}</FieldError>
        </form>

        {/* (b) Correo externo validado */}
        <form
          className="flex flex-col gap-1.5"
          onSubmit={(e) => void onAgregarCorreo(e)}
          noValidate
        >
          <Label htmlFor="aviso-correo">Añadir un correo externo</Label>
          <div className="flex gap-2">
            <Input
              id="aviso-correo"
              type="email"
              autoComplete="off"
              placeholder="persona@empresa.com"
              value={correo}
              aria-invalid={errorCorreo ? true : undefined}
              onChange={(e) => {
                setCorreo(e.target.value);
                setErrorCorreo(null);
              }}
            />
            <Button type="submit" disabled={agregar.isPending || correo.trim() === ""}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Añadir
            </Button>
          </div>
          <FieldError>{errorCorreo}</FieldError>
        </form>
      </div>
    </div>
  );
}

// mensajeError extrae un mensaje legible del error del backend (409 duplicado, 422…).
function mensajeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.fields?.email) {
      return err.fields.email;
    }
    return err.message || fallback;
  }
  return `${fallback} Comprueba tu conexión.`;
}
